import type {
  FamilyData,
  FamilyDashboardStats,
  MemberLimitCheck,
} from "@/hooks/useOptimizedFamily";
import type { User } from "@supabase/supabase-js";
import type { Family, FamilyGoal, FamilyLicense, FamilyMember } from "@/services/familyService";

export interface FamilyHookViewModelInput {
  isDevPreview: boolean;
  devPreview: {
    family: Family;
    familyMembers: FamilyMember[];
    familyGoals: FamilyGoal[];
    user: User;
  } | null;
  familyData: FamilyData | undefined;
  isLoading: boolean;
  isFetched: boolean;
  error: Error | null;
  user: User | null;
  createPending: boolean;
  createGoalPending: boolean;
  addMemberPending: boolean;
}

/**
 * Resolve Family Hook View Model.
 */
export function resolveFamilyHookViewModel(input: FamilyHookViewModelInput) {
  if (input.isDevPreview && input.devPreview) {
    return {
      family: input.devPreview.family,
      familyMembers: input.devPreview.familyMembers,
      familyGoals: input.devPreview.familyGoals,
      familyLicense: null as FamilyLicense | null,
      isLoading: false,
      loading: false,
      isFetched: true,
      error: null as string | null,
      familyLoadError: null as string | null,
      user: input.devPreview.user,
    };
  }

  return {
    family: input.familyData?.family ?? null,
    familyMembers: input.familyData?.familyMembers ?? [],
    familyGoals: input.familyData?.familyGoals ?? [],
    familyLicense: input.familyData?.familyLicense || null,
    isLoading:
      input.isLoading || input.createPending || input.createGoalPending || input.addMemberPending,
    loading: input.isLoading,
    isFetched: input.isFetched,
    error: input.error?.message || input.familyData?.loadError || null,
    familyLoadError: input.familyData?.loadError || null,
    user: input.user,
  };
}

/**
 * Toast Family Created.
 */
export function toastFamilyCreated(panEncryptionSkipped: boolean): {
  title: string;
  description: string;
} {
  if (panEncryptionSkipped) {
    return {
      title: "Family created",
      description:
        "Your family profile was created. PAN could not be encrypted — add it later from your profile after signing in again.",
    };
  }
  return {
    title: "Success",
    description: "Family created successfully",
  };
}

/**
 * Load Family Dashboard Stats For Hook.
 */
export async function loadFamilyDashboardStatsForHook(options: {
  isDevPreview: boolean;
  previewMemberCount: number;
  previewGoalCount: number;
  familyData: FamilyData | undefined;
  buildLocal: (data: FamilyData | undefined) => FamilyDashboardStats;
  buildPreview: (members: number, goals: number) => FamilyDashboardStats;
  mergeRemote: (
    local: FamilyDashboardStats,
    remote: Partial<FamilyDashboardStats> & { total_goals?: number; net_worth?: number }
  ) => FamilyDashboardStats;
  fetchRemote: (
    familyId: string
  ) => Promise<Partial<FamilyDashboardStats> & { total_goals?: number; net_worth?: number }>;
}): Promise<FamilyDashboardStats> {
  if (options.isDevPreview) {
    return options.buildPreview(options.previewMemberCount, options.previewGoalCount);
  }

  const localStats = options.buildLocal(options.familyData);
  const familyId = options.familyData?.family?.id;
  if (!familyId) return localStats;

  try {
    const remoteStats = await options.fetchRemote(familyId);
    return options.mergeRemote(localStats, remoteStats);
  } catch (error) {
    console.warn("Failed to load family dashboard stats; using local fallback", error);
    return localStats;
  }
}

/**
 * Resolve Member Limit Check.
 */
export function resolveMemberLimitCheck(options: {
  isDevPreview: boolean;
  previewMemberCount: number;
  familyId: string | undefined;
  buildPreviewLimit: (count: number) => MemberLimitCheck;
  checkLimit: (familyId: string) => Promise<MemberLimitCheck>;
}): Promise<MemberLimitCheck> {
  if (options.isDevPreview) {
    return Promise.resolve(options.buildPreviewLimit(options.previewMemberCount));
  }
  if (!options.familyId) {
    return Promise.reject(new Error("No family found"));
  }
  return options.checkLimit(options.familyId);
}
